import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { pm, hrms, getFileUrl } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { theme } from '../../theme';

const ACCENTS = ['#F97316','#3B82F6','#8B5CF6','#EC4899','#14B8A6','#F59E0B','#22C55E','#EF4444'];
const accentFor = (name = '') => ACCENTS[(name.charCodeAt(0) || 0) % ACCENTS.length];
const initials  = (name = '') => name.split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase() || '?';

const MEMBER_ROLES = ['project_manager','scrum_master','tech_lead','team_lead','member'];
const ADMIN_ROLES  = ['super_admin','hr_admin','hr_head','ceo','cfo','cto'];
const MANAGE_ROLES = ['project_manager','scrum_master','tech_lead','team_lead'];
const PROJECT_MODIFY_ROLES = ['ceo','cto','cfo','project_manager','team_lead'];

const ROLE_META = {
  project_manager: { label:'Project Manager', color:'#7C3AED', bg:'#F5F3FF' },
  scrum_master:    { label:'Scrum Master',    color:'#1D4ED8', bg:'#EFF6FF' },
  tech_lead:       { label:'Tech Lead',       color:'#0891B2', bg:'#ECFEFF' },
  team_lead:       { label:'Team Lead',       color:'#0D9488', bg:'#F0FDFA' },
  member:          { label:'Member',          color:'#374151', bg:'#F3F4F6' },
};

const inputStyle = {
  padding:'0.45rem 0.65rem', border:'1px solid #E4E4E7', borderRadius:7,
  fontSize:'0.82rem', background:'#fff', boxSizing:'border-box', width:'100%', outline:'none',
};

/* ── Employee search combobox ── */
function EmployeeSearch({ employees, value, onChange }) {
  const [query,  setQuery]  = useState('');
  const [open,   setOpen]   = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef(null);

  const selected = employees.find(e => e.id === value);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    if (!q) return employees.slice(0, 50);
    return employees.filter(e =>
      e.full_name?.toLowerCase().includes(q) ||
      e.emp_code?.toLowerCase().includes(q) ||
      e.department?.toLowerCase().includes(q)
    ).slice(0, 50);
  }, [query, employees]);

  useEffect(() => {
    const handler = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = emp => { onChange(emp.id); setQuery(''); setOpen(false); setActive(-1); };
  const clear  = ()  => { onChange(''); setQuery(''); setOpen(false); };

  const handleKey = e => {
    if (!open) { if (e.key === 'ArrowDown' || e.key === 'Enter') setOpen(true); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a+1, filtered.length-1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActive(a => Math.max(a-1, 0)); }
    if (e.key === 'Enter')     { e.preventDefault(); if (active >= 0 && filtered[active]) select(filtered[active]); }
    if (e.key === 'Escape')    { setOpen(false); setActive(-1); }
  };

  return (
    <div ref={wrapRef} style={{ position:'relative', flex:1, minWidth:200 }}>
      <div style={{ display:'flex', alignItems:'center',
                    border:`1px solid ${open ? '#6366F1' : '#E4E4E7'}`, borderRadius:7,
                    background:'#fff', overflow:'hidden',
                    boxShadow: open ? '0 0 0 2px #6366F120' : 'none' }}>
        {selected && !open ? (
          <div style={{ flex:1, padding:'0.45rem 0.65rem', fontSize:'0.82rem', color:'#111827',
                        display:'flex', alignItems:'center', gap:'0.5rem', overflow:'hidden' }}>
            <span style={{ fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {selected.full_name}
            </span>
            <span style={{ fontSize:'0.72rem', color:'#9CA3AF', fontFamily:'monospace', flexShrink:0 }}>
              {selected.emp_code}
            </span>
          </div>
        ) : (
          <input value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); setActive(-1); }}
            onFocus={() => setOpen(true)} onKeyDown={handleKey}
            placeholder="Search by name or emp code…"
            style={{ flex:1, padding:'0.45rem 0.65rem', border:'none', outline:'none',
                     fontSize:'0.82rem', background:'transparent', color:'#111827', minWidth:0 }} />
        )}
        {selected
          ? <button type="button" onClick={clear}
              style={{ padding:'0 0.5rem', border:'none', background:'none', cursor:'pointer',
                       color:'#9CA3AF', fontSize:'1rem', lineHeight:1, flexShrink:0 }}>×</button>
          : <span style={{ padding:'0 0.5rem', color:'#9CA3AF', fontSize:'0.75rem',
                           flexShrink:0, pointerEvents:'none' }}>▾</span>
        }
      </div>
      {open && (
        <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, zIndex:50,
                      background:'#fff', border:'1px solid #E4E4E7', borderRadius:8,
                      boxShadow:'0 6px 24px rgba(0,0,0,0.1)', maxHeight:240, overflowY:'auto' }}>
          {filtered.length === 0 ? (
            <div style={{ padding:'0.75rem 1rem', fontSize:'0.8rem', color:'#9CA3AF', textAlign:'center' }}>
              No employees found
            </div>
          ) : filtered.map((emp, i) => {
            const ac = accentFor(emp.full_name);
            return (
              <div key={emp.id} onMouseDown={() => select(emp)} onMouseEnter={() => setActive(i)}
                style={{ padding:'0.5rem 0.875rem', cursor:'pointer', fontSize:'0.82rem',
                         display:'flex', alignItems:'center', gap:'0.625rem',
                         background: i === active ? '#F5F3FF' : 'transparent',
                         borderBottom: i < filtered.length-1 ? '1px solid #F3F4F6' : 'none' }}>
                <div style={{ width:28, height:28, borderRadius:'50%', flexShrink:0,
                              background:`${ac}18`, color:ac,
                              display:'flex', alignItems:'center', justifyContent:'center',
                              fontWeight:800, fontSize:'0.68rem' }}>
                  {initials(emp.full_name)}
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:600, color:'#111827', overflow:'hidden',
                                textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {emp.full_name}
                  </div>
                  <div style={{ fontSize:'0.7rem', color:'#9CA3AF', fontFamily:'monospace' }}>
                    {emp.emp_code}{emp.department ? ` · ${emp.department}` : ''}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Member avatar ── */
function Avatar({ name, photo, size = 48 }) {
  const ac = accentFor(name);
  const [imgErr, setImgErr] = useState(false);
  const url = getFileUrl(photo);

  if (url && !imgErr) {
    return (
      <img src={url} alt={name}
        onError={() => setImgErr(true)}
        style={{ width:size, height:size, borderRadius:'50%', objectFit:'cover',
                 flexShrink:0, border:`2px solid ${ac}30` }} />
    );
  }
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background:`${ac}18`, color:ac,
                  fontWeight:800, fontSize: size > 40 ? '0.9rem' : '0.72rem',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  flexShrink:0, border:`2px solid ${ac}30` }}>
      {initials(name)}
    </div>
  );
}

/* ── Main Members page ── */
export default function Members({ project }) {
  const { user } = useAuth();
  const [members,   setMembers]   = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [newEmpId,  setNewEmpId]  = useState('');
  const [newRole,   setNewRole]   = useState('member');
  const [saving,    setSaving]    = useState(false);

  const canManage = useMemo(() => {
    if (ADMIN_ROLES.includes(user?.role) || PROJECT_MODIFY_ROLES.includes(user?.role)) return true;
    if (project?.manager_id != null && user?.emp_id != null &&
        Number(project.manager_id) === Number(user.emp_id)) return true;
    const mine = members.find(m => m.employee_id != null && user?.emp_id != null &&
      Number(m.employee_id) === Number(user.emp_id));
    return mine ? MANAGE_ROLES.includes(mine.role) : false;
  }, [user, project, members]);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      pm.getMembers(project.id),
      hrms.getEmployees({ employment_status:'active', limit:500 }),
    ]).then(([mRes, eRes]) => {
      setMembers(mRes.data || []);
      const d = eRes.data;
      setEmployees(Array.isArray(d) ? d : d.items || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const addMember = async e => {
    e.preventDefault();
    if (!newEmpId) return;
    setSaving(true);
    try {
      await pm.addMember(project.id, { employee_id: Number(newEmpId), role: newRole });
      setNewEmpId('');
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to add member');
    } finally { setSaving(false); }
  };

  const updateRole = async (m, role) => {
    await pm.updateMember(project.id, m.id, { role })
      .catch(err => alert(err.response?.data?.detail || 'Failed'));
    load();
  };

  const removeMember = async m => {
    if (!window.confirm(`Remove ${m.employee_name || 'this member'} from the project?`)) return;
    await pm.removeMember(project.id, m.id)
      .catch(err => alert(err.response?.data?.detail || 'Failed'));
    load();
  };

  const existingIds    = new Set(members.map(m => m.employee_id));
  const availableEmps  = employees.filter(e => !existingIds.has(e.id));

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center',
                  padding:'4rem', color:'#9CA3AF', gap:'0.75rem' }}>
      <div style={{ width:28, height:28, border:'3px solid #E4E4E7',
                    borderTopColor:theme.primary, borderRadius:'50%',
                    animation:'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      Loading members…
    </div>
  );

  return (
    <div style={{ padding:'1.5rem', display:'flex', flexDirection:'column', gap:'1.25rem' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'0.75rem' }}>
        <div>
          <h2 style={{ margin:0, fontSize:'1.05rem', fontWeight:700, color:'#111827' }}>
            Project Members
          </h2>
          <p style={{ margin:'0.2rem 0 0', fontSize:'0.78rem', color:'#9CA3AF' }}>
            {members.length} member{members.length !== 1 ? 's' : ''} in this project
          </p>
        </div>
      </div>

      {/* Member grid */}
      {members.length === 0 ? (
        <div style={{ textAlign:'center', padding:'3rem', color:'#9CA3AF',
                      background:'#fff', borderRadius:12, border:'1px solid #E4E4E7' }}>
          <div style={{ fontSize:'2rem', marginBottom:'0.5rem', opacity:0.3 }}>👥</div>
          <p style={{ fontWeight:600, color:'#111827', margin:'0 0 0.25rem' }}>No members yet</p>
          <p style={{ fontSize:'0.78rem', margin:0 }}>Add team members below to collaborate on this project.</p>
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:'0.875rem' }}>
          {members.map(m => {
            const name = m.employee_name || m.name || '—';
            const rm   = ROLE_META[m.role] || ROLE_META.member;
            return (
              <div key={m.id}
                style={{ background:'#fff', border:'1px solid #E4E4E7', borderRadius:12,
                         padding:'1rem 1.125rem', display:'flex', alignItems:'center',
                         gap:'0.875rem', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
                <Avatar name={name} photo={m.profile_photo} size={48} />
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:'0.875rem', fontWeight:700, color:'#111827',
                                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {name}
                  </div>
                  {m.emp_code && (
                    <div style={{ fontSize:'0.7rem', color:'#9CA3AF', fontFamily:'monospace' }}>
                      {m.emp_code}{m.department ? ` · ${m.department}` : ''}
                    </div>
                  )}
                  {/* Role: editable for managers, badge-only otherwise */}
                  {canManage ? (
                    <select value={m.role} onChange={e => updateRole(m, e.target.value)}
                      style={{ marginTop:'0.35rem', padding:'2px 6px',
                               border:`1px solid ${rm.color}40`, borderRadius:6,
                               fontSize:'0.72rem', background:rm.bg, color:rm.color,
                               cursor:'pointer', fontWeight:700, outline:'none' }}>
                      {MEMBER_ROLES.map(r => (
                        <option key={r} value={r}>{ROLE_META[r]?.label || r.replace('_',' ')}</option>
                      ))}
                    </select>
                  ) : (
                    <span style={{ display:'inline-block', marginTop:'0.35rem',
                                   background:rm.bg, color:rm.color, padding:'2px 8px',
                                   borderRadius:9999, fontSize:'0.68rem', fontWeight:700 }}>
                      {rm.label}
                    </span>
                  )}
                </div>
                {canManage && (
                  <button onClick={() => removeMember(m)}
                    style={{ padding:'0.3rem 0.65rem', border:'1px solid #FEE2E2', borderRadius:7,
                             background:'#fff', cursor:'pointer', fontSize:'0.72rem',
                             fontWeight:600, color:'#EF4444', flexShrink:0 }}
                    onMouseEnter={e => e.currentTarget.style.background='#FEE2E2'}
                    onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                    Remove
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add member */}
      {canManage && availableEmps.length > 0 && (
        <form onSubmit={addMember}
          style={{ background:'#fff', border:'1px dashed #E4E4E7', borderRadius:12,
                   padding:'1.125rem 1.25rem', display:'flex', gap:'0.75rem',
                   flexWrap:'wrap', alignItems:'flex-end' }}>
          <p style={{ margin:'0 0 0.5rem', fontSize:'0.78rem', fontWeight:700,
                      color:'#6B7280', width:'100%', textTransform:'uppercase',
                      letterSpacing:'0.05em' }}>
            Add Team Member
          </p>
          <EmployeeSearch employees={availableEmps} value={newEmpId} onChange={setNewEmpId} />
          <div>
            <div style={{ fontSize:'0.7rem', fontWeight:700, color:'#6B7280',
                          textTransform:'uppercase', letterSpacing:'0.04em', marginBottom:4 }}>
              Role
            </div>
            <select value={newRole} onChange={e => setNewRole(e.target.value)}
              style={{ ...inputStyle, width:'auto' }}>
              {MEMBER_ROLES.map(r => (
                <option key={r} value={r}>{ROLE_META[r]?.label || r.replace('_',' ')}</option>
              ))}
            </select>
          </div>
          <button type="submit" disabled={!newEmpId || saving}
            style={{ padding:'0.5rem 1.125rem', border:'none', borderRadius:8,
                     background: (!newEmpId || saving) ? '#E4E4E7' : theme.primary,
                     color: (!newEmpId || saving) ? '#9CA3AF' : '#fff',
                     cursor: (!newEmpId || saving) ? 'not-allowed' : 'pointer',
                     fontWeight:700, fontSize:'0.82rem', alignSelf:'flex-end', whiteSpace:'nowrap' }}>
            {saving ? 'Adding…' : '+ Add Member'}
          </button>
        </form>
      )}
    </div>
  );
}
